/**
 * Session Storage Cache for ODB Service Information
 * 
 * Caches ODB service information data from UAT API calls to improve performance and reduce redundant requests.
 * Cache expires after 5 minutes.
 * 
 * Cache key format: `odbServiceInfo-{environment}-{productId}-{startDate}-{endDate}`
 */

import type { Environment } from './EnvironmentSelector';
import type { UATODBServiceInformation } from '../../../services/fixedRouteUatClient';

const CACHE_PREFIX = 'odbServiceInfo';
const CACHE_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes

interface CachedODBServiceInfoData {
  data: UATODBServiceInformation[];
  timestamp: number;
  environment: Environment;
  productId: string;
  startDate: string;
  endDate: string;
}

/**
 * Get cached ODB service information data if it exists and hasn't expired
 * 
 * @param environment - Environment (UAT or Production)
 * @param productId - Product ID
 * @param startDate - Start date (YYYY-MM-DD)
 * @param endDate - End date (YYYY-MM-DD)
 * @returns Cached data or null if not found or expired
 */
export function getCachedODBServiceInfoList(
  environment: Environment,
  productId: string,
  startDate: string,
  endDate: string
): UATODBServiceInformation[] | null {
  try {
    const cacheKey = `${CACHE_PREFIX}-${environment}-${productId}-${startDate}-${endDate}`;
    const cachedStr = sessionStorage.getItem(cacheKey);
    
    if (!cachedStr) {
      return null;
    }
    
    const cached: CachedODBServiceInfoData = JSON.parse(cachedStr);
    const now = Date.now();
    
    // Check if cache is expired
    if (now - cached.timestamp > CACHE_EXPIRATION_MS) {
      sessionStorage.removeItem(cacheKey);
      return null;
    }
    
    // Verify cache matches current parameters
    if (cached.environment !== environment ||
        cached.productId !== productId ||
        cached.startDate !== startDate ||
        cached.endDate !== endDate) {
      // Cache key mismatch, remove it
      sessionStorage.removeItem(cacheKey);
      return null;
    }
    
    console.log(`[getCachedODBServiceInfoList] Cache hit for ${cacheKey}`);
    return cached.data;
  } catch (error) {
    console.warn('[getCachedODBServiceInfoList] Error reading cache:', error);
    return null;
  }
}

/**
 * Cache ODB service information data in session storage
 * 
 * @param environment - Environment (UAT or Production)
 * @param productId - Product ID
 * @param startDate - Start date (YYYY-MM-DD)
 * @param endDate - End date (YYYY-MM-DD)
 * @param data - ODB service information data to cache
 */
export function setCachedODBServiceInfoList(
  environment: Environment,
  productId: string,
  startDate: string,
  endDate: string,
  data: UATODBServiceInformation[]
): void {
  try {
    const cacheKey = `${CACHE_PREFIX}-${environment}-${productId}-${startDate}-${endDate}`;
    const cached: CachedODBServiceInfoData = {
      data,
      timestamp: Date.now(),
      environment,
      productId,
      startDate,
      endDate,
    };
    
    sessionStorage.setItem(cacheKey, JSON.stringify(cached));
    console.log(`[setCachedODBServiceInfoList] Cached ODB service information for ${cacheKey}`);
  } catch (error) {
    console.warn('[setCachedODBServiceInfoList] Error writing cache:', error);
    // Cache write failures are not critical, just log and continue
  }
}

/**
 * Clear cached ODB service information for specific parameters
 * 
 * @param environment - Environment (UAT or Production)
 * @param productId - Product ID
 * @param startDate - Start date (YYYY-MM-DD)
 * @param endDate - End date (YYYY-MM-DD)
 */
export function clearCachedODBServiceInfoList(
  environment: Environment,
  productId: string,
  startDate: string,
  endDate: string
): void {
  try {
    const cacheKey = `${CACHE_PREFIX}-${environment}-${productId}-${startDate}-${endDate}`;
    sessionStorage.removeItem(cacheKey);
    console.log(`[clearCachedODBServiceInfoList] Cleared cache for ${cacheKey}`);
  } catch (error) {
    console.warn('[clearCachedODBServiceInfoList] Error clearing cache:', error);
  }
}

/**
 * Clear all ODB service information cache entries
 */
export function clearAllODBServiceInfoCache(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    console.log(`[clearAllODBServiceInfoCache] Cleared ${keysToRemove.length} cache entries`);
  } catch (error) {
    console.warn('[clearAllODBServiceInfoCache] Error clearing cache:', error);
  }
}

